import type {
  SurveyQuestion,
  SurveyCategory,
  RouteSurveyConfig,
} from '../types/survey-config.types';
import type {
  Answer,
  QuestionWithAnswer,
  CategoryWithAnswers,
  CreateRouteSurveyRequest,
} from '../types/route-survey.types';
import {
  SURVEY_POSITIVE_SCORE_THRESHOLD,
  ENPS_QUESTION,
  ENPS_CATEGORY,
  ENPS_CATEGORIES,
  ENPS_SCORE_RANGES,
} from '../constants/survey.constants';

/**
 * Get the CSS class name for a survey option based on its score
 */
export const getOptionClassName = (score: number, isEnps: boolean): string => {
  if (isEnps) {
    // eNPS buttons use a single blue scheme to avoid selection bias
    return 'route-survey-add-option-enps-blue';
  } else {
    return score > SURVEY_POSITIVE_SCORE_THRESHOLD
      ? 'route-survey-add-option-positive'
      : 'route-survey-add-option-negative';
  }
};

/**
 * Flatten all questions from categories into a single array with their category context
 */
export const flattenQuestions = (
  config: RouteSurveyConfig,
): Array<{ question: SurveyQuestion; category: SurveyCategory }> => {
  const allQuestions: Array<{
    question: SurveyQuestion;
    category: SurveyCategory;
  }> = [];

  config.categories.forEach((category) => {
    category.questions.forEach((question) => {
      allQuestions.push({ question, category });
    });
  });

  // Add eNPS question at the end
  allQuestions.push({ question: ENPS_QUESTION, category: ENPS_CATEGORY });

  return allQuestions;
};

/**
 * Transform flat answers map to nested structure for backend submission
 */
export const transformAnswersToSubmission = (
  answers: Map<number, Answer>,
  config: RouteSurveyConfig,
): CreateRouteSurveyRequest => {
  const categoriesWithAnswers: CategoryWithAnswers[] = [];

  // Iterate over each category and each question within the category
  config.categories.forEach((category) => {
    const questionsWithAnswers: QuestionWithAnswer[] = [];

    category.questions.forEach((question) => {
      const answer = answers.get(question.id);
      if (answer) {
        questionsWithAnswers.push({
          id: question.id,
          answer: {
            optionId: answer.optionId,
            score: answer.score,
            ...(answer.comment && { comment: answer.comment }),
          },
        });
      }
    });

    if (questionsWithAnswers.length > 0) {
      categoriesWithAnswers.push({
        id: category.id,
        questions: questionsWithAnswers,
      });
    }
  });

  // Extract eNPS answer separately (not part of categories)
  const enpsAnswer = answers.get(ENPS_QUESTION.id);

  // eNPS score is required, so we need to handle the case where it's missing
  if (!enpsAnswer) {
    throw new Error('eNPS score is required to submit the survey');
  }

  return {
    categories: categoriesWithAnswers,
    enpsScore: enpsAnswer.optionId, // The option ID represents the 0-10 score
    enpsComments: enpsAnswer.comment,
  };
};

/**
 * Get eNPS category based on score (0-10)
 * - Detractors: 0-6
 * - Passives: 7-8
 * - Promoters: 9-10
 */
export const getEnpsCategory = (score: number): string => {
  if (
    score >= ENPS_SCORE_RANGES.PROMOTER.min &&
    score <= ENPS_SCORE_RANGES.PROMOTER.max
  ) {
    return ENPS_CATEGORIES.PROMOTER;
  }
  if (
    score >= ENPS_SCORE_RANGES.PASSIVE.min &&
    score <= ENPS_SCORE_RANGES.PASSIVE.max
  ) {
    return ENPS_CATEGORIES.PASSIVE;
  }
  return ENPS_CATEGORIES.DETRACTOR;
};

/**
 * Check if eNPS score is a detractor (0-6)
 */
export const isEnpsDetractor = (score: number): boolean => {
  return (
    score >= ENPS_SCORE_RANGES.DETRACTOR.min &&
    score <= ENPS_SCORE_RANGES.DETRACTOR.max
  );
};

/**
 * Get the color for eNPS score display based on the calculated eNPS value
 * - Red (danger-dark-1): eNPS < 0
 * - Null: eNPS > 0
 */
export const getEnpsScoreColor = (enpsScore: number): string | null => {
  if (enpsScore < 0) {
    return 'var(--danger-dark-1)';
  }
  return null;
};
